import * as angular from 'angular';
import * as _ from 'lodash';

import {
    IValueViewModel,
    IdentifierType,
    Identifier,
    IGroupTreeNode,
    Pattern,
    Group,
    ComponentDefinition,
    IIdentifierVm,
    IDisplayedColumn,
    IMappingDictionary,
    IdentifierDumbTableRowButton
} from '../../models';
import { IIdentifierStateService, IExportToCsvService, IStoreService } from '../../services';
import { guidEmpty, identifierSaved, identifierSavedForce } from '../../common';
import { Store, StoreElement } from '../../stateManagement';

class IdentifiersGlobalListController implements angular.IComponentController {
    allIdentifiers: Identifier[];
    criteria: Pattern<IValueViewModel>[];
    displayChildren: boolean;
    displayCount: number;
    displayed: IValueViewModel[];
    groups: Group[];
    hasMore: boolean;
    hasLess: boolean;
    filtered: IValueViewModel[];
    filterable: IValueViewModel[];
    identifiers: Identifier[];
    identifiersVm: IIdentifierVm[];
    identifierType = IdentifierType;
    isLoading: boolean;
    itemsCount: number;
    searchString: string;
    exposedScope: {
        more: () => void;
        less: () => void;
        exportToCsv: () => void;
    };
    valueViewModels: IValueViewModel[];

    readonly buttons: IdentifierDumbTableRowButton[];
    readonly initialFilter: string;
    readonly menu: IDisplayedColumn[];
    readonly selectedGroup: any;
    readonly selectedIdentifier: Identifier;

    private _pageNumber = 0;
    private _pageSize = 48;
    private _pageCount: number = 0;
    private _all: IIdentifierVm[] = [];
    private _garbages: (() => void)[] = [];

    static $inject = [
        '$q',
        '$rootScope',
        '$scope',
        'storeService',
        'identifierStateService',
        'exportToCsvService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$rootScope: angular.IRootScopeService,
        private _$scope: angular.IScope,
        private _storeService: IStoreService,
        private _identifierStateService: IIdentifierStateService,
        private _exportToCsvService: IExportToCsvService
    ) {
    }

    $onInit() {
        this.exposedScope = {
            more: this.more.bind(this),
            less: this.less.bind(this),
            exportToCsv: this.exportToCsv.bind(this)
        };
        this.displayed = [];

        this.hasMore = false;
        this.hasLess = false;

        this._garbages.push(this._$scope.$watchCollection(() => this.filtered, () => {
            if (this.filtered) {
                this.resetPages(this.filtered.length);
                this.updateDisplay();
            }
        }));

        this._garbages.push(this._$rootScope.$on(identifierSaved, () => {
            this.refresh(false);
        }));

        this._garbages.push(this._$rootScope.$on(identifierSavedForce, () => {
            this.refresh(true);
            this.updateDisplay();
        }));

        this._garbages.push(this._$scope.$watch(() => this.displayChildren, () => {
            this.refreshFiltered();
        }));

        this._garbages.push(this._storeService.register(
            store => this.reloadStore(store),
            [StoreElement.Identifiers, StoreElement.Groups, StoreElement.ValueViewModels]
        ));

        this.refresh();
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        if (onChangesObj['selectedGroup']?.currentValue && this.allIdentifiers && this.valueViewModels) {
            const selectedGroup = this.selectedGroup;
            this.reselectGroup(selectedGroup, this.allIdentifiers, this.valueViewModels);
        }

        if (onChangesObj['menu']?.currentValue) {
            if (this.menu && this.menu.length > 0) {
                this.initializePropertyMapper(this.menu);
            }
        }
    }

    refresh(force?: boolean): void {
        this.isLoading = true;
        this._$q
            .all([
                this._storeService.dispatchers().valueViewModels.getAll(force),
                this._storeService.dispatchers().groups.getAll(force)
            ])
            .finally(() => {
                this.isLoading = false;
            });
    }

    reloadStore(store: Store) {
        const identifiers = _.values<Identifier>(store.identifiers.dictionary);
        const valueViewModels = _.values<IValueViewModel>(store.valueViewModels.dictionary);
        // const vmList = valueViewModels.filter(i => i.objectType === ObjectType.Identifier);
        this.allIdentifiers = identifiers;
        this.valueViewModels = valueViewModels;
        this.reselectGroup(this.selectedGroup, this.allIdentifiers, this.valueViewModels);
        this.isLoading = false;
    }

    more() {
        if (this._pageNumber < this._pageCount - 1) {
            this._pageNumber++;
            this.updateDisplay();
        }
    }

    less() {
        if (this._pageNumber > 0) {
            this._pageNumber--;
            this.updateDisplay();
        }
    }

    exportToCsv() {
        const data = this._exportToCsvService.getCsvContent(this.menu, this.filtered, this._propertyMapper);
        this._exportToCsvService.getCsvFromData(data, 'identifiers.csv');
    }

    private _propertyMapper: IMappingDictionary<IValueViewModel & { linked?: IValueViewModel }>;

    private initializePropertyMapper(menu: IDisplayedColumn[]) {
        this._propertyMapper = {};
        if (menu) {
            menu.forEach(m => this._propertyMapper[m.property] = v => v[m.property]);
        }
        this._propertyMapper['tag'] = v => v?.tagName ? v.tagName : v.linked ? v.linked.name : null;
        this._propertyMapper['tagName'] = this._propertyMapper['tag'];
        this._propertyMapper['type'] = v => v.variableTypeName;
        this._propertyMapper['subType'] = v => v.variableSubTypeName;
        this._propertyMapper['qualifier'] = v => v.qualifierName;
        this._propertyMapper['unit'] = v => v.unitName;
    }

    private reselectGroup(selectedGroup: IGroupTreeNode, identifiers: Identifier[], viewModels: IValueViewModel[]) {
        let all: Identifier[];
        if (selectedGroup) {
            all = selectedGroup.identifierCollection;
            let children = [];
            if (selectedGroup.children) {
                children = this.mapIdentifiers(selectedGroup);
            }
            //Map to identifiers from store;
            all = children.concat(all)
                .map(i => _.find(identifiers, is => is.guid === i.guid));
        }
        else {
            all = identifiers;
        }

        const merged = this._identifierStateService.mergeViewModels(all, viewModels);
        const sorted = _.sortBy(merged, d => d.groupUniqueName);
        this.identifiersVm = merged;
        this._all = sorted;
        this.filterable = this.pureFilter(selectedGroup, this._all, this.displayChildren);
        return { merged, sorted };
    }

    private mapIdentifiers(node: IGroupTreeNode): Identifier[] {
        const identifiers = _.flatten(node.children.map(c => {
            c.identifierCollection.forEach(i => (i as any).group = c);
            return c.identifierCollection;
        }));
        const children = _.flatten(node.children.filter(c => c.children).map(c => this.mapIdentifiers(c)));
        return identifiers.concat(children);
    }

    private refreshFiltered() {
        this.filterable = this.pureFilter(this.selectedGroup, this._all, this.displayChildren);
    }

    private pureFilter(selectedGroup: Group, all: IIdentifierVm[], displayChildren: boolean): IIdentifierVm[] {
        const selectedGuid = selectedGroup
            ? selectedGroup.guid
            : guidEmpty;
        const filterable = displayChildren
            ? all.filter(t => true)
            : all.filter(i => !!i.groupList && i.groupList[0].guid === selectedGuid);
        return filterable;
    }

    private updateDisplay() {
        const displayed = this.page(this.filtered);
        this.displayCount = displayed.length;
        this.displayed = displayed;
    }

    private page(list: IValueViewModel[]): IValueViewModel[] {
        const displayed = _.take(list, (this._pageNumber + 1) * this._pageSize);
        this.hasMore = this._pageNumber < this._pageCount - 1;
        this.hasLess = this._pageNumber > 0;
        return displayed;
    }

    private resetPages(itemsCount: number): void {
        this.itemsCount = itemsCount;
        this._pageCount = Math.ceil(itemsCount / this._pageSize);
        this.hasLess = false;
        this.hasMore = this._pageCount > 1;
        this._pageNumber = 0;
    }
}

export const wtIdentifiersGlobalList: ComponentDefinition = {
    componentId: 'wtIdentifiersGlobalList',
    controller: IdentifiersGlobalListController,
    template: require('./identifiersGlobalList.component.html'),
    controllerAs: 'vm',
    bindings: {
        buttons: '<?',
        displayCount: '=',
        displayed: '=?',
        exposedScope: '=?',
        hasLess: '=',
        hasMore: '=',
        initialFilter: '<',
        itemsCount: '=',
        menu: '<',
        selectedGroup: '<?',
        selectedIdentifier: '<'
    }
};